/**
 * LocalStorage utility for persisting UX preferences
 * Centralized storage keys and helpers for consistent localStorage usage
 */

// Storage keys - centralized to avoid typos and conflicts
export const STORAGE_KEYS = {
  // Manual page
  MANUAL_SIDEBAR_OPEN: 'ux:manual-sidebar-open',
  MANUAL_LAST_DOC: 'ux:manual-last-doc',
  
  // Calendar
  CALENDAR_VIEW: 'ux:calendar-view',
  CALENDAR_SIDEBAR_OPEN: 'ux:calendar-sidebar-open',
  // Calendari v2: pestanyes (una propietat) o totes seguides (`tabs` / `all`,
  // plans/dia-a-dia-calendari-i-whatsapp.md §2.1).
  CALENDAR_PROPERTY_VIEW: 'ux:calendar-property-view',
  // Calendari v2: la pestanya triada. Es manté el nom antic (sense `ux:`) perquè no es perdi la
  // tria desada; el valor ja era un número en text, que `JSON.parse` llegeix igual.
  CALENDAR_V2_PROPERTY_ID: 'calendarV2_propertyId',
  
  // Tables & Lists
  TABLE_PAGE_SIZE: 'ux:table-page-size',
  
  // Sidebar principal
  MAIN_SIDEBAR_COLLAPSED: 'ux:main-sidebar-collapsed',
  
  // Theme
  THEME: 'ux:theme',

  // WhatsApp: com obrir els links, per dispositiu i no per config global del servidor
  // (plans/expedia-precontact.md §F9). En Pere fa servir Web a Windows i l'app al mòbil.
  WHATSAPP_OPEN_MODE: 'ux:whatsapp-open-mode',

  // Inbox de WhatsApp (`/whatsapp`): panell dret obert o plegat i seccions plegades
  // (plans/whatsapp-inbox.md §4.4).
  WHATSAPP_INBOX_PANEL: 'ux:whatsapp-inbox-panel',

  // Inbox de WhatsApp: interruptor «Signatura» del compositor, per usuari
  // (`{ [userId]: boolean }`, plans/whatsapp-inbox.md §4.3).
  WHATSAPP_INBOX_SIGNATURE: 'ux:whatsapp-inbox-signature',

  // Filters (per page)
  FILTERS_RESERVATIONS: 'ux:filters-reservations',
  FILTERS_GUESTS: 'ux:filters-guests',
  FILTERS_CLIENTS: 'ux:filters-clients',
} as const;

type StorageKey = typeof STORAGE_KEYS[keyof typeof STORAGE_KEYS];

/**
 * Type-safe localStorage wrapper with JSON serialization
 */
export const storage = {
  /**
   * Get a value from localStorage
   * @param key - Storage key from STORAGE_KEYS
   * @param defaultValue - Default value if key doesn't exist
   */
  get<T>(key: StorageKey, defaultValue: T): T {
    try {
      const item = localStorage.getItem(key);
      if (item === null) return defaultValue;
      return JSON.parse(item) as T;
    } catch {
      return defaultValue;
    }
  },

  /**
   * Set a value in localStorage
   * @param key - Storage key from STORAGE_KEYS
   * @param value - Value to store (will be JSON serialized)
   */
  set<T>(key: StorageKey, value: T): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.warn(`Failed to save to localStorage: ${key}`, error);
    }
  },

  /**
   * Remove a value from localStorage
   * @param key - Storage key from STORAGE_KEYS
   */
  remove(key: StorageKey): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn(`Failed to remove from localStorage: ${key}`, error);
    }
  },

  /**
   * Clear all UX-related storage (keys starting with 'ux:')
   */
  clearAll(): void {
    try {
      Object.values(STORAGE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
    } catch (error) {
      console.warn('Failed to clear localStorage', error);
    }
  },

  /**
   * Get a boolean value (common for toggles)
   */
  getBoolean(key: StorageKey, defaultValue: boolean): boolean {
    return this.get(key, defaultValue);
  },

  /**
   * Toggle a boolean value and return the new state
   */
  toggleBoolean(key: StorageKey, currentValue: boolean): boolean {
    const newValue = !currentValue;
    this.set(key, newValue);
    return newValue;
  },
};

// Note: For React hook version, use the dedicated hooks file
// This keeps the storage utility framework-agnostic
